import { Combobox, Transition } from '@headlessui/react';

import buildJson from '@/config/build.json';
import { getEnvironment, xplorSpotLiteTitle, xplorSpotTitle, xplorTraderTitle } from '@/helpers/environmentHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectAppVersion } from '@/state/reducers/appVersionSlice';
import {
    Account,
    selectAccountType,
    selectCredentials,
    selectCurrentAccount,
    selectCurrentAccountName,
    selectCurrentEntityName,
    setCurrentAccount,
    setCurrentAccountName,
    setCurrentEntityName
} from '@/state/reducers/authSlice';
import { openHelper } from '@/state/reducers/helperSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import useXplorPortal from '@/utils/hooks/useXplorPortal';
import { navigateAccordingToAccountType } from '@/utils/navigation';
import { SSOLoadingState, useSso } from '@/utils/providers/SSOProvider';
import { showLayoutSwitch } from '@/utils/route';
import { default as cn } from 'classnames';
import { ButtonHTMLAttributes, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconType } from 'react-icons/lib';
import {
    LuActivity,
    LuArrowDownToLine,
    LuArrowUpFromLine,
    LuChevronDown,
    LuChevronUp,
    LuPieChart,
    LuRepeat,
    LuSliders,
    LuTrendingUp,
    LuZap
} from 'react-icons/lu';
import {
    MdExpandMore,
    MdOutlineAccountBalance,
    MdOutlineAccountBalanceWallet,
    MdOutlineAccountBox,
    MdOutlineHelpOutline,
    MdOutlineLogout,
    MdOutlineSettings
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Divider from '../common/Divider';
import { ssoDisabledMessage } from './DesktopNavbar';
import { onLogout, onUserChange } from './Navbar';

const env = getEnvironment();

const MobileDrawer = (props: any) => {
    const { settingsDisclosure, drawerDisclosure } = props;
    const dispatch = useAppDispatch();

    const accountType = useAppSelector(selectAccountType);
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const currentAccountName = useAppSelector(selectCurrentAccountName);
    const currentEntityName = useAppSelector(selectCurrentEntityName);
    const { currentVersion } = useAppSelector(selectAppVersion);

    const [query, setQuery] = useState('');

    const lang = useLanguage();
    const navigate = useNavigate();
    const { onXplorPortal } = useXplorPortal(credentials);

    const portalPages: {
        key: string;
        label: string;
        Icon: IconType;
        path: string;
        permissions: string[];
    }[] = useMemo(() => {
        const platforms = [
            {
                key: 'Overview',
                label: lang.navigation.overview,
                Icon: LuPieChart,
                path: '/portal',
                permissions: ['ACCOUNT_VIEW']
            },
            {
                key: 'Deposits',
                label: lang.navigation.deposits,
                Icon: LuArrowDownToLine,
                path: '/portal/deposits',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_DEPOSIT']
            },
            {
                key: 'Withdrawals',
                label: lang.navigation.withdrawals,
                Icon: LuArrowUpFromLine,
                path: '/portal/withdrawals',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_WITHDRAWAL']
            },
            {
                key: 'Trades',
                label: lang.navigation.trades,
                Icon: LuActivity,
                path: '/portal/trades',
                permissions: ['ACCOUNT_VIEW']
            },
            {
                key: 'Instruments',
                label: lang.navigation.instruments,
                Icon: LuSliders,
                path: '/portal/instruments',
                permissions: ['ACCOUNT_VIEW']
            },
            {
                key: 'FX Swaps',
                label: lang.navigation['fx swaps'],
                Icon: LuRepeat,
                path: '/portal/fx-swaps',
                permissions: ['ACCOUNT_VIEW']
            },
            {
                key: 'Bank Accounts',
                label: lang.navigation['bank accounts'],
                Icon: MdOutlineAccountBalance,
                path: '/portal/bank-accounts',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_WHITELIST']
            },
            {
                key: 'Wallets',
                label: lang.navigation.wallets,
                Icon: MdOutlineAccountBalanceWallet,
                path: '/portal/wallets',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_WHITELIST']
            }
            // { label: 'Profile', Icon: MdOutlinePerson, path: '/portal/profile' }
        ];

        return platforms.filter((platform) => {
            if (accountType === 'MARGIN') return platform.key !== 'Wallets';
            else if (accountType === 'NOP') return platform.key !== 'FX Swaps';
            return true;
        });
    }, [accountType, lang]);

    const { flattenEntities, isSSOEnabled, isLoadingSSO } = useSso();

    const currentAccountType = useMemo(() => {
        const properties = credentials?.accounts.find((account) => account.code === currentAccount)?.properties || [];
        return properties.find((property) => property.key === 'TYPE')?.value || 'NOP';
    }, [credentials, currentAccount]);
    const canOpenHelper = location.pathname === '/' || location.pathname.startsWith('/trader');

    const convertedAccounts = useMemo(() => {
        return (
            credentials?.accounts?.map((account) => ({
                code: account.code,
                name: account.name,
                properties: account.properties,
                entityName: flattenEntities?.find(
                    (entity) =>
                        entity.nameMappings?.find((nameMapping) => nameMapping.source === 'CELER')?.name ===
                        account.name
                )?.entityName
            })) || []
        );
    }, [credentials, flattenEntities]);

    const gcexAccountName = useMemo(() => {
        if (flattenEntities && !currentEntityName) {
            return (
                flattenEntities.find(
                    (entity) =>
                        entity.nameMappings?.find((nameMapping) => nameMapping.source === 'CELER')?.name ===
                        currentAccountName
                )?.entityName || currentAccountName
            );
        } else return currentAccountName;
    }, [flattenEntities, currentAccountName, currentEntityName]);

    const filteredAccounts =
        query === ''
            ? convertedAccounts
            : convertedAccounts.filter((account) =>
                  (account.entityName || account.name).toLowerCase().includes(query.toLowerCase())
              );

    // Tracks whether the scrollable nav list has more content above/below
    // the visible viewport, so we can render fade + arrow cues.
    const navScrollRef = useRef<HTMLDivElement>(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);

    const updateScrollCues = useCallback(() => {
        const el = navScrollRef.current;
        if (!el) return;
        const hasOverflow = el.scrollHeight > el.clientHeight + 1;
        setCanScrollUp(hasOverflow && el.scrollTop > 1);
        setCanScrollDown(hasOverflow && el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    }, []);

    useEffect(() => {
        updateScrollCues();

        const el = navScrollRef.current;
        if (!el) return;

        const resizeObserver = new ResizeObserver(updateScrollCues);
        resizeObserver.observe(el);

        window.addEventListener('resize', updateScrollCues);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateScrollCues);
        };
        // Re-check whenever nav content changes size (e.g. layout switch items toggling).
    }, [updateScrollCues, portalPages, showLayoutSwitch]);

    return (
        <>
            <div
                className={cn(
                    'block lg:hidden h-full fixed z-30 right-0 top-0 w-3/4 sm:w-2/4 bg-brand-background-dark shadow-lg transform transition-transform duration-300 ease-in-out',
                    { ['translate-x-0']: drawerDisclosure[0], ['translate-x-full']: !drawerDisclosure[0] }
                )}>
                {/* <div className="absolute text-brand-neutral-400 right-4 top-4">
                    <MdClose className="w-4 h-4" />
                </div> */}
                {/* Drawer content */}
                <div className="flex flex-col h-full justify-between">
                    <div className="flex flex-col flex-1 basis-0">
                        <div className="flex flex-col">
                            <div className="flex flex-col gap-2 py-4 text-brand-text">
                                <div className="text-brand-neutral-400 text-xs px-4">{lang.portal.account}</div>
                                <Combobox
                                    value={{
                                        code: currentAccount,
                                        name: currentAccountName
                                    }}
                                    onChange={(account: any) => {
                                        dispatch(setCurrentAccount(account.code));
                                        dispatch(setCurrentAccountName(account.name));
                                        dispatch(setCurrentEntityName(account.entityName));
                                        onUserChange(credentials, dispatch);
                                        const curr = credentials?.accounts.find(
                                            (acc) => acc.code === account.code
                                        ) as Account;
                                        navigateAccordingToAccountType(curr, navigate);
                                    }}>
                                    <div className="relative min-w-[150px] w-full">
                                        <div className="flex items-center px-4 py-2">
                                            <MdOutlineAccountBox className="w-5 h-5 flex-shrink-0" />
                                            <Combobox.Input
                                                className="text-sm truncate pl-4 bg-transparent outline-none w-full"
                                                displayValue={(account: any) => account?.entityName || account?.name}
                                                onChange={(event) => setQuery(event.target.value)}
                                            />

                                            <Combobox.Button className="ml-auto">
                                                <MdExpandMore className="w-5 h-5" />
                                            </Combobox.Button>
                                        </div>

                                        <Transition
                                            as={Fragment}
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0">
                                            <Combobox.Options className="absolute z-[60] max-h-60 w-full overflow-auto rounded-md bg-brand-background-dark border border-brand-neutral-700 py-1 text-sm shadow-lg shadow-brand-neutral-900">
                                                {filteredAccounts.length === 0 ? (
                                                    <div className="px-4 py-2 text-brand-neutral-400">
                                                        No results found
                                                    </div>
                                                ) : (
                                                    filteredAccounts.map((account, accountIdx) => (
                                                        <Combobox.Option
                                                            key={accountIdx}
                                                            value={account}
                                                            className={({ active }) =>
                                                                cn(
                                                                    'relative cursor-pointer select-none flex gap-4 py-2 px-4',
                                                                    {
                                                                        'bg-brand-primary': active
                                                                    }
                                                                )
                                                            }>
                                                            <MdOutlineAccountBox className="w-5 h-5 flex-shrink-0" />

                                                            <span className="block truncate">
                                                                {account.entityName || account.name}
                                                            </span>
                                                        </Combobox.Option>
                                                    ))
                                                )}
                                            </Combobox.Options>
                                        </Transition>
                                    </div>
                                </Combobox>
                            </div>
                            <Divider className="bg-brand-accent-light" />
                        </div>
                        <div className="relative flex-1 basis-0 min-h-0">
                            {/* Top scroll cue: fade + arrow shown when content is scrolled down */}
                            <div
                                aria-hidden="true"
                                className={cn(
                                    'pointer-events-none absolute top-0.5 inset-x-0 flex justify-center z-10 transition-opacity duration-150',
                                    canScrollUp ? 'opacity-100' : 'opacity-0'
                                )}>
                                <LuChevronUp className="w-4 h-4 text-brand-text mt-2 animate-bounce" />
                            </div>

                            <div ref={navScrollRef} onScroll={updateScrollCues} className="py-4 h-full overflow-auto">
                                {portalPages.map(({ Icon, label, path }, index) => (
                                    <DrawerItemButton
                                        key={index}
                                        label={label}
                                        Icon={Icon}
                                        disabled={!isSSOEnabled}
                                        disabledMessage={ssoDisabledMessage(isLoadingSSO)}
                                        isLoadingSSO={isLoadingSSO}
                                        onClick={() => {
                                            if (location.pathname !== path) navigate(path);
                                            drawerDisclosure[1].close();
                                        }}
                                    />
                                ))}
                                {currentAccountType === 'NOP' && (
                                    <Fragment>
                                        <DrawerItemButton
                                            label={xplorSpotTitle}
                                            Icon={LuTrendingUp}
                                            onClick={() => navigate('/')}
                                            disabledMessage={`Margin accounts are not supported in ${xplorSpotTitle}`}
                                        />
                                        <DrawerItemButton
                                            label={xplorSpotLiteTitle}
                                            Icon={LuZap}
                                            onClick={() => navigate('/lite')}
                                            disabledMessage={`Margin accounts are not supported in ${xplorSpotLiteTitle}`}
                                        />
                                    </Fragment>
                                )}
                                {currentAccountType === 'MARGIN' && (
                                    <DrawerItemButton
                                        label={xplorTraderTitle}
                                        Icon={LuTrendingUp}
                                        onClick={() => navigate('/trader')}
                                        disabledMessage={`Margin accounts are not supported in ${xplorTraderTitle}`}
                                    />
                                )}
                                <DrawerItemButton
                                    label={lang.navigation.settings}
                                    Icon={MdOutlineSettings}
                                    onClick={settingsDisclosure[1].toggle}
                                />
                                {canOpenHelper && (
                                    <DrawerItemButton
                                        label={lang.navigation.helper}
                                        Icon={MdOutlineHelpOutline}
                                        onClick={() => {
                                            dispatch(openHelper());
                                            drawerDisclosure[1].close();
                                        }}
                                    />
                                )}
                            </div>

                            {/* Bottom scroll cue: fade + arrow shown when more content is below */}
                            <div
                                aria-hidden="true"
                                className={cn(
                                    'pointer-events-none absolute bottom-0.5 inset-x-0 flex justify-center z-10 transition-opacity duration-150',
                                    canScrollDown ? 'opacity-100' : 'opacity-0'
                                )}>
                                <LuChevronDown className="w-4 h-4 text-brand-text animate-bounce" />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <div className="flex gap-3 p-4 text-brand-text font-semibold text-xs">
                            {env && env !== 'prod' && (
                                <div className="w-fit cursor-default">
                                    <div className="py-1 px-3 rounded-md uppercase bg-brand-primary">{env}</div>
                                </div>
                            )}
                            <div className="py-1 px-3 rounded-md bg-brand-neutral-700">
                                v{currentVersion}{' '}
                                <span className="text-brand-neutral-400 text-xs">({buildJson.build})</span>
                            </div>
                        </div>

                        <Divider className="bg-brand-accent-light" />
                        <div className="flex flex-col gap-2 py-4 text-brand-text">
                            <div className="text-brand-neutral-400 text-xs px-4">{credentials?.username}</div>
                            <DrawerItemButton
                                label={lang.navigation.logout}
                                Icon={MdOutlineLogout}
                                onClick={() => onLogout(credentials, dispatch)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Overlay */}
            {drawerDisclosure[0] && (
                <div
                    className="block lg:hidden fixed z-20 inset-0 bg-black opacity-50"
                    onClick={drawerDisclosure[1].close}
                />
            )}
        </>
    );
};

export default MobileDrawer;

interface DrawerItemButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    Icon: IconType;
    label: string;
    disabledMessage?: string;
    isLoadingSSO?: SSOLoadingState;
    requiresSSO?: boolean;
}

const DrawerItemButton = (props: DrawerItemButtonProps) => {
    const { Icon, label, disabledMessage, isLoadingSSO = 'idle', requiresSSO = false, ...restProps } = props;
    const { disabled } = restProps;

    const ssoTooltip = useMemo(() => {
        if (isLoadingSSO === 'pending') return 'We are currently connecting this feature via SSO...';
        if (isLoadingSSO === 'error') return 'This feature is not available due to an error in SSO.';
        return '';
    }, [isLoadingSSO]);

    const tooltip = useMemo(() => {
        if (disabled) return ssoTooltip || disabledMessage;
        return ssoTooltip;
    }, [disabled, disabledMessage, ssoTooltip]);

    return (
        <button
            title={tooltip}
            className={cn('group flex w-full gap-4 items-center px-4 py-2 text-sm', {
                'text-brand-neutral-400 cursor-not-allowed': disabled,
                'text-brand-text hover:bg-brand-primary': !disabled
            })}
            {...restProps}>
            <span>
                <Icon className="h-5 w-5" />
            </span>
            <div className="relative flex items-center justify-center">
                {isLoadingSSO === 'pending' && requiresSSO && <Ping isLoadingSSO={isLoadingSSO} />}
                {isLoadingSSO === 'error' && requiresSSO && <Ping isLoadingSSO={isLoadingSSO} />}
                <div
                    className={cn('truncate whitespace-nowrap', {
                        'pl-4': requiresSSO && ['pending', 'error'].includes(isLoadingSSO)
                    })}>
                    {label}
                </div>
            </div>
        </button>
    );
};

export const Ping = ({ isLoadingSSO }: { isLoadingSSO: SSOLoadingState }) => {
    const ping = useMemo(() => {
        switch (isLoadingSSO) {
            case 'pending':
                return {
                    dot: 'bg-yellow-500',
                    wave: 'bg-yellow-400'
                };
            case 'error':
                return {
                    dot: 'bg-brand-red',
                    wave: 'bg-brand-red-light'
                };
            default:
                return false;
        }
    }, [isLoadingSSO]);

    return (
        <Fragment>
            {ping && (
                <Fragment>
                    <span
                        className={cn(
                            'z-[2] absolute top-[5px] left-0 h-2.5 w-2.5 rounded-full opacity-75 animate-ping',
                            {
                                [ping.wave]: true
                            }
                        )}
                    />
                    <span
                        className={cn(
                            'z-[2] absolute top-[5px] left-0 h-2.5 w-2.5 rounded-full border-[1px] border-brand-background text-[6px]',
                            { [ping.dot]: true }
                        )}
                    />
                </Fragment>
            )}
        </Fragment>
    );
};
